import { useEffect, useRef } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

/**
 * Reset the form to fresh initialValues on every open=false→true transition.
 *
 * Replaces the prior useFormSession key-remount approach: there is no remount,
 * focus is preserved, scroll position is preserved.
 *
 * Same observable semantics: opening the panel always starts pristine at the
 * latest initialValues. While open stays true, intermediate prop updates do
 * NOT clobber user input.
 */
export function useResetOnOpen<TValues extends FieldValues>(
  methods: UseFormReturn<TValues>,
  open: boolean,
  initialValues: TValues | undefined,
  options: {
    /**
     * Skip the reset when the form is *already* open on first render, because
     * `useForm` seeded `defaultValues` from the same `initialValues` on that
     * render. The reset would only repeat that work — and it runs in an effect,
     * i.e. after children have registered their own fields, so a field a child
     * registered during the first render gets wiped by a value object that
     * predates it. Panels mount closed and so never hit this; a dialog whose
     * parent mounts it already open does, on every mount.
     */
    skipInitial?: boolean;
  } = {},
): void {
  const wasOpenRef = useRef(options.skipInitial ? open : false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      methods.reset(initialValues, {
        keepDefaultValues: false,
        keepValues: false,
        keepDirty: false,
        keepTouched: false,
        keepErrors: false,
        keepIsSubmitted: false,
        keepSubmitCount: false,
        keepIsValid: false,
      });
    }
    wasOpenRef.current = open;
  }, [open, initialValues, methods]);
}
